import { lightBlue, green, red, orange, grey } from '@mui/material/colors';

/**
 * ==============================================================================
 * Marketrack Design Tokens
 * 3-Tier Token Architecture (Material Design 3 & Uber Base Design System aligned)
 * ==============================================================================
 * 
 * TIER 1: Reference Tokens (Global Primitives)
 * Raw values sourced from official Material UI v5 primitives and the M2/M3 dark
 * elevation ladder. These carry zero semantic intent on their own.
 */
export const referenceTokens = Object.freeze({
  color: Object.freeze({
    // Standard Material UI color primitives
    blue: Object.freeze({
      300: lightBlue[300], // #4FC3F7
      400: lightBlue[400], // #29B6F6
      700: lightBlue[700], // #0288D1
    }),
    green: Object.freeze({
      300: green[300], // #81C784
      400: green[400], // #66BB6A
      700: green[700], // #388E3C
    }),
    red: Object.freeze({
      300: red[300], // #E57373
      400: red[400], // #EF5350
      700: red[700], // #D32F2F
    }),
    orange: Object.freeze({
      300: orange[300], // #FFB74D
      400: orange[400], // #FFA726
      700: orange[700], // #F57C00
    }),
    grey: Object.freeze({
      900: grey[900], // #212121
      800: grey[800], // #424242
      700: grey[700], // #616161
      600: grey[600], // #757575
      500: grey[500], // #9E9E9E
    }),

    // Material Dark Theme Elevation Ladder (Semi-transparent white over #121212)
    elevation: Object.freeze({
      0: '#121212',  // Base canvas (0% overlay)
      1: '#1E1E1E',  // Tooltip / popover body (5% overlay)
      2: '#222222',  // Elevated container (7% overlay)
      3: '#232323',  // Side navbar / search input surface (8% overlay)
      6: '#2C2C2C',  // Metric card / container surface (11% overlay)
      12: '#333333', // Subtle border / elevated panel (14% overlay)
      16: '#444444', // Default border (15% overlay)
      24: '#383838', // Highly elevated dialog / modal surface (16% overlay)
    }),

    // Common base primitives
    common: Object.freeze({
      black: '#000000',
      white: '#FFFFFF',
    }),

    // Alpha primitives for high-contrast typography, dividers, and action states
    alpha: Object.freeze({
      white05: 'rgba(255, 255, 255, 0.05)',
      white10: 'rgba(255, 255, 255, 0.10)',
      white12: 'rgba(255, 255, 255, 0.12)',
      white38: 'rgba(255, 255, 255, 0.38)',
      white70: 'rgba(255, 255, 255, 0.70)',
      white100: '#FFFFFF',
    }),
  }),

  // Systematic border widths
  borders: Object.freeze({
    width: Object.freeze({
      none: 0,
      thin: 1,    // 1px standard container outline
      medium: 2,  // 2px active tab accent / focus ring outline
      thick: 4,   // 4px callout bar / alert indicator
    }),
  }),

  // Systematic 4px grid spacing scale
  spacing: Object.freeze({
    1: 4,
    2: 8,
    3: 12,
    4: 16,
    5: 20,
    6: 24,
    8: 32,
    10: 40,
    12: 48,
  }),

  typography: Object.freeze({
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    fontSize: Object.freeze({
      xs: 10,
      sm: 12,
      caption: 13,
      body2: 14,
      body1: 16,
      h6: 20,
      h5: 24,
      h4: 34,
      h3: 48,
      h2: 60,
      h1: 96,
    }),
    fontWeight: Object.freeze({
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      heavy: 800,
    }),
  }),

  shape: Object.freeze({
    borderRadiusSm: 2,
    borderRadiusMd: 4,
    borderRadiusLg: 8,
    borderRadius: 4,
  }),
});

/**
 * TIER 2: Semantic Tokens (System Roles)
 * Meaningful design decisions mapping strictly to Tier 1 reference tokens.
 * Zero hardcoded hex values are allowed in this tier.
 */
export const semanticTokens = Object.freeze({
  // Surface roles for backgrounds and containers
  surfaces: Object.freeze({
    base: referenceTokens.color.elevation[0],           // #121212
    dark: referenceTokens.color.elevation[3],           // #232323 (Sidebar, search)
    card: referenceTokens.color.elevation[6],           // #2C2C2C (Cards, metric tiles)
    subtle: referenceTokens.color.elevation[1],         // #1E1E1E (Tooltips, popovers)
    elevated: referenceTokens.color.elevation[24],      // #383838 (Dialogs, modals)
  }),

  // Border roles for structural outlines and dividers (Single standard border #444444)
  borders: Object.freeze({
    width: referenceTokens.borders.width,
    default: referenceTokens.color.elevation[16],       // #444444
    divider: referenceTokens.color.alpha.white12,       // rgba(255, 255, 255, 0.12)
    subtle: referenceTokens.color.elevation[16],        // Backward compat alias
    strong: referenceTokens.color.elevation[16],        // Backward compat alias
  }),

  // Interactive brand identity
  brand: Object.freeze({
    primary: referenceTokens.color.blue[400],           // #29B6F6
    primaryHover: referenceTokens.color.blue[700],      // #0288D1
    primaryLight: referenceTokens.color.blue[300],      // #4FC3F7
    contrastText: referenceTokens.color.common.black,
  }),

  // Semantic status feedback (Market gains, losses, alerts, errors)
  status: Object.freeze({
    positive: referenceTokens.color.green[400],         // #66BB6A
    positiveLight: referenceTokens.color.green[300],
    positiveDark: referenceTokens.color.green[700],
    negative: referenceTokens.color.red[400],           // #EF5350
    negativeLight: referenceTokens.color.red[300],
    negativeDark: referenceTokens.color.red[700],
    error: referenceTokens.color.red[400],              // #EF5350 (MUI default error)
    warning: referenceTokens.color.orange[400],         // #FFA726
    warningLight: referenceTokens.color.orange[300],
    warningDark: referenceTokens.color.orange[700],
    info: referenceTokens.color.blue[400],              // #29B6F6
  }),

  // Typography contrast scale
  text: Object.freeze({
    primary: referenceTokens.color.alpha.white100,      // #FFFFFF
    secondary: referenceTokens.color.alpha.white70,     // rgba(255, 255, 255, 0.70)
    muted: referenceTokens.color.grey[600],             // #757575
    disabled: referenceTokens.color.alpha.white38,      // rgba(255, 255, 255, 0.38)
  }),

  // Alias for backward compatibility
  primary: Object.freeze({
    main: referenceTokens.color.blue[400],
    light: referenceTokens.color.blue[300],
    dark: referenceTokens.color.blue[700],
    contrastText: referenceTokens.color.common.black,
  }),

  // Interactive action states for hover, selection, and focus
  action: Object.freeze({
    hover: referenceTokens.color.alpha.white05,
    selected: referenceTokens.color.alpha.white10,
    focus: referenceTokens.color.alpha.white12,
  }),

  spacing: referenceTokens.spacing,
  typography: referenceTokens.typography,
  shape: referenceTokens.shape,
});

export const brandTokens = semanticTokens;
export default brandTokens;
